<template>
  <BaseDialog
    v-model="visible"
    title="Add Payment Method"
    :loading="loading"
    :disabled="isFormInvalid"
    @confirm="onConfirm"
    @cancel="onCancel"
    confirmLabel="Save"
    position="standard"
    class="billing-payments__dialog"
  >
    <div class="billing-payments__dialog-form">
      <div class="billing-payments__form-field">
        <label class="billing-payments__form-label">Cardholder Name<span class="required-asterisk">*</span></label>
        <q-input
          :value="fullName"
          @input="$emit('update:fullName', $event)"
          outlined
          dense
          placeholder="Enter cardholder name"
          required
          :error="!!nameError"
          :error-message="nameError"
          class="billing-payments__form-input"
          @blur="validateName"
        />
      </div>

      <div class="billing-payments__form-field">
        <label class="billing-payments__form-label">Card Information<span class="required-asterisk">*</span></label>
        <div id="card-element" class="billing-payments__card-element" :class="{ 'card-error': cardError }"></div>
        <p v-if="cardError" class="billing-payments__error-message">{{ cardError }}</p>
        <p v-else-if="error" class="billing-payments__error-message">{{ error }}</p>
      </div>
    </div>
  </BaseDialog>
</template>

<script>
import BaseDialog from '@/components/base/BaseDialog.vue';

export default {
  name: 'PaymentMethodDialog',
  components: { BaseDialog },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: ''
    },
    fullName: {
      type: String,
      default: ''
    },
    cardElement: {
      type: Object,
      default: null
    }
  },
  model: {
    prop: 'value',
    event: 'input'
  },
  emits: ['update:fullName', 'confirm', 'input'],
  data() {
    return {
      nameError: '',
      cardError: '',
      cardComplete: false
    };
  },
  computed: {
    visible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    },
    isFormInvalid() {
      return !this.fullName || this.nameError || !this.cardComplete || this.cardError || this.loading;
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        // Reset validation state when dialog opens
        this.nameError = '';
        this.cardError = '';
        this.cardComplete = false;
      }
    },
    // Add a watcher to clear error when name is updated
    fullName() {
      if (this.nameError) {
        this.validateName();
      }
    }
  },
  mounted() {
    // Listen for card events from parent when card element is mounted
    this.$root.$on('stripe:card-error', this.setCardError);
    this.$root.$on('stripe:card-complete', this.setCardComplete);
  },
  beforeDestroy() {
    // Clean up event listeners
    this.$root.$off('stripe:card-error', this.setCardError);
    this.$root.$off('stripe:card-complete', this.setCardComplete);
  },
  methods: {
    onConfirm() {
      // Validate before confirming
      this.validateName();

      if (this.isFormInvalid) {
        return;
      }

      this.$emit('confirm');
    },
    onCancel() {
      this.visible = false;
    },
    validateName() {
      if (!this.fullName || this.fullName.trim() === '') {
        this.nameError = 'Cardholder name is required';
        return false;
      } else if (this.fullName.length < 3) {
        this.nameError = 'Name must be at least 3 characters';
        return false;
      } else {
        this.nameError = '';
        return true;
      }
    },
    setCardError(error) {
      this.cardError = error;
    },
    setCardComplete(complete) {
      this.cardComplete = complete;
    }
  }
};
</script>

<style lang="scss" scoped>
@import './billing-payments-styles.scss';

.required-asterisk {
  color: #d32f2f;
  margin-left: 2px;
}

.card-error {
  border: 1px solid #d32f2f;
}

.billing-payments__error-message {
  color: #d32f2f;
  font-size: 12px;
  margin-top: 4px;
  margin-bottom: 0;
}
</style>
